/**
 * 前端 OCR：使用 PaddleOCR（paddleocr-browser + ONNXRuntime Web + OpenCV）辨識圖片中的中英文，供參加人員名單等用途。
 * 僅在瀏覽器端執行，不上傳後端。
 */

const PADDLE_ASSETS_BASE =
  'https://cdn.jsdelivr.net/npm/paddleocr-browser/dist/';

let paddleInitPromise: Promise<void> | null = null;

/** 等待全域的 ort / cv / Paddle 載入（由 index.html 的 script 提供） */
async function waitForPaddleGlobals(timeoutMs = 10000): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('PaddleOCR 僅支援瀏覽器環境');
  }
  if (import.meta.env.DEV) {
    console.debug('[PaddleOCR] waitForPaddleGlobals start');
  }
  const start = Date.now();

  while (true) {
    const w = window as unknown as {
      Paddle?: unknown;
      ort?: unknown;
      cv?: unknown;
    };
    if (w.Paddle && w.ort && w.cv) return;
    if (Date.now() - start > timeoutMs) {
      throw new Error('PaddleOCR 依賴尚未載入（ort / cv / Paddle）');
    }

    await new Promise(resolve => setTimeout(resolve, 100));
  }
}

async function initPaddleOcr(): Promise<void> {
  if (paddleInitPromise) return paddleInitPromise;
  paddleInitPromise = (async () => {
    await waitForPaddleGlobals();
    const w = window as unknown as {
      Paddle: { init: (opts: unknown) => Promise<void> };
      ort: unknown;
      cv: unknown;
    };

    const dicResp = await fetch(`${PADDLE_ASSETS_BASE}ppocr_keys_v1.txt`);
    if (!dicResp.ok) throw new Error('載入 PaddleOCR 字典失敗');
    const dic = await dicResp.text();
    if (import.meta.env.DEV) {
      console.debug('[PaddleOCR] dic loaded', dic.slice(0, 40));
    }

    if (import.meta.env.DEV) {
      console.debug('[PaddleOCR] Paddle.init start');
    }
    await w.Paddle.init({
      detPath: `${PADDLE_ASSETS_BASE}ppocr_det.onnx`,
      recPath: `${PADDLE_ASSETS_BASE}ppocr_rec.onnx`,
      dic,
      ort: w.ort,
      cv: w.cv,
      node: false,
    });
    if (import.meta.env.DEV) {
      console.debug('[PaddleOCR] Paddle.init done');
    }
  })();
  return paddleInitPromise;
}

/**
 * 從圖片檔案辨識文字（中+英）。
 *
 * @param imageFile - 圖片檔案（支援常見格式）
 * @returns 辨識出的整段文字（多行以 \n 分隔）
 */
export async function recognizeTextFromImage(imageFile: File): Promise<string> {
  if (import.meta.env.DEV) {
    console.debug('[PaddleOCR] recognize start', {
      name: imageFile.name,
      size: imageFile.size,
      type: imageFile.type,
    });
  }
  await initPaddleOcr();
  const dataUrl = await fileToDataUrl(imageFile);
  if (import.meta.env.DEV) {
    console.debug('[PaddleOCR] dataUrl length', dataUrl.length);
  }
  const w = window as unknown as {
    Paddle: { ocr: (input: string) => Promise<unknown> };
  };
  const result = await w.Paddle.ocr(dataUrl);
  if (import.meta.env.DEV) {
    console.debug('[PaddleOCR] raw result', result);
  }
  return normalizePaddleResult(result);
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(String(reader.result ?? ''));
    };
    reader.onerror = () => {
      reject(new Error('讀取圖片失敗'));
    };
    reader.readAsDataURL(file);
  });
}

function normalizePaddleResult(result: unknown): string {
  if (import.meta.env.DEV) {
    console.debug('[PaddleOCR] normalize input', result);
  }
  if (result == null) return '';
  if (typeof result === 'string') return result.trim();

  const texts: string[] = [];

  const collect = (node: unknown) => {
    if (node == null) return;
    if (typeof node === 'string') {
      const t = node.trim();
      if (t) texts.push(t);
      return;
    }
    if (Array.isArray(node)) {
      for (const item of node) {
        collect(item);
      }
      return;
    }
    if (typeof node === 'object') {
      const obj = node as Record<string, unknown>;
      for (const [key, value] of Object.entries(obj)) {
        if (key === 'text' && typeof value === 'string') {
          const t = value.trim();
          if (t) texts.push(t);
        } else {
          collect(value);
        }
      }
    }
  };

  collect(result);

  const out = texts.join('\n');
  if (import.meta.env.DEV) {
    console.debug('[PaddleOCR] normalize output', out);
  }
  return out;
}

/**
 * 將 OCR 辨識結果依換行拆成一行一筆，過濾空白與純數字行。
 *
 * @param text - OCR 回傳的整段文字
 * @returns 非空的行陣列（已 trim）
 */
export function parseLinesFromOcrText(text: string): string[] {
  const lines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0);
  return lines.filter(line => !/^\d+$/.test(line) && line.length <= 50);
}

/** 常見活動資訊關鍵字（非人名），若行內含這些則視為非名字 */
const NON_NAME_KEYWORDS = [
  '地點',
  '時間',
  '場地',
  '費用',
  '人數',
  '限制',
  '共計',
  '男生',
  '女生',
  '羽球館',
  '晚上',
  '選球隊',
  '大天使',
  '轉',
  '說',
  '點還',
  '全還',
  '全以',
  '第.',
  '場地說',
  '人數限制',
];

/** 名字長度上限（過長多為句子或標題） */
const MAX_NAME_LENGTH = 12;

/**
 * 從 OCR 拆出的行中，篩出「較可能為人名」的項目，排除活動說明、時間、地點、費用等。
 * 規則：先去掉前綴編號與括號備註，再檢查長度、關鍵字與是否含中文。
 *
 * @param lines - parseLinesFromOcrText 的回傳
 * @returns 較可能為人名的字串陣列（已去除前綴編號與括號）
 */
export function filterLikelyNames(lines: string[]): string[] {
  const results: string[] = [];

  for (const raw of lines) {
    let line = raw.trim();
    if (!line) continue;

    // 去掉前綴編號，例如「1 阿德」「23 阿章」
    line = line.replace(/^\d+\s*/, '');
    // 去掉尾巴括號備註，例如「阿德 (季)」「阿章（F）」→「阿德」「阿章」
    line = line.replace(/\s*[（(][^）)]*[）)]\s*$/g, '').trim();

    if (line.length < 2 || line.length > MAX_NAME_LENGTH) continue;

    const hasChinese = /[\u4e00-\u9fff]/.test(line);
    const isAsciiName =
      !hasChinese && /^[A-Za-z][A-Za-z\s.'-]*$/.test(line) && line.length >= 2;
    // 至少要有一個中文字，或是看起來像英文名字；避免 BE / RE 這類純英文縮寫
    if (!hasChinese && !isAsciiName) continue;
    // 排除「標籤:值」或「標籤：值」
    if (/^[^\s:：]+[：:]\s*/.test(line)) continue;
    // 排除含金額（數字+$、￥、元）
    if (/\d+\s*[$￥元]|費用|男生\d|女生\d/.test(line)) continue;
    // 排除像日期（2026、3/7、3月）
    if (/^\d{4}$|^\d{1,2}\/\d{1,2}|^\d+[月日]/.test(line)) continue;
    // 排除純數字或結尾為數字（常見為編號）
    if (/^\d+$|^\d+[六日]$/.test(line)) continue;
    // 排除含活動關鍵字
    if (NON_NAME_KEYWORDS.some(kw => line.includes(kw))) continue;

    results.push(line);
  }

  return results;
}

/**
 * 將 OCR 辨識結果依換行拆成一行一筆的名稱，過濾空白與純數字行。
 * @deprecated 請改用 parseLinesFromOcrText + filterLikelyNames 以排除活動資訊
 */
export function parseNamesFromOcrText(text: string): string[] {
  return parseLinesFromOcrText(text);
}
